import React from 'react';
import {Button, Col, DatePicker, Input, Row, Form} from 'antd';
import {EnvironmentOutlined} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

import {dstOffSetDateCheck, getBookingIncrement} from '../../../../helpers/booking';
import {dateTimeFormat, timeFormat} from '../../../../lib/time';

import './BookingMapFilterForm.less';

declare type EventValue<DateType> = DateType | null;
declare type RangeValue<DateType> = [EventValue<DateType>, EventValue<DateType>] | null;
const {RangePicker} = DatePicker;
const mobileMode = window.innerWidth <= 575;

interface FormValues {
    "date-range-picker": [Dayjs | null, Dayjs | null];
    "location-input": string;
}

interface BookingMapFilterFormProps {
    bookingMapState: {
        startedAt: Dayjs | null;
        endedAt: Dayjs | null;
        locationInput: string;
    };
    handleBookingMapSearch: (value: FormValues) => void;
}

const BookingMapFilterForm = ({bookingMapState, handleBookingMapSearch}: BookingMapFilterFormProps) => {
    const [form] = Form.useForm();

    React.useEffect(() => {
    // *** This fires everytime window size is adjusted.
    // If previous bookingMapState exists from rerender or onClickBack, repopulate the fields with the retained bookingMapState.

        form.setFieldsValue({
            "date-range-picker": [bookingMapState.startedAt, bookingMapState.endedAt],
            "location-input": bookingMapState.locationInput,
        });
    }, [bookingMapState, form]);

    const onDateFilterChange = (dates: RangeValue<Dayjs>) => {
        let start, end;
        // Triggered when datepicker cleared but event is null, so need to do array check.
        if (Array.isArray(dates) && dates[0] && dates[1]) {
            [start] = dstOffSetDateCheck({
                start: dayjs(dates[0]),
                end: dayjs(dates[1]),
                isStart: true,
                format: dateTimeFormat
            });
            [start, end] = dstOffSetDateCheck({
                start: start,
                end: dayjs(dates[1]),
                isStart: false,
                format: dateTimeFormat
            });

            form.setFieldsValue({'date-range-picker': [start, end]});
        }
    }

    const bookingIncrementMin = Number(getBookingIncrement()) / 60;
    const timePickerProps = {
        showTime: {
            minuteStep: bookingIncrementMin,
            use12Hours: true,
            format: timeFormat,
        },
        format: dateTimeFormat,
    };

    return (
        <Form
            form={form}
            className="booking-map-filter-form-container"
            onFinish={handleBookingMapSearch}
        >
            <Row gutter={5}>
                <Col xs={24} sm={10} md={10} lg={10}  className="booking-map-filter-form-full-width">
                    <Form.Item name="location-input">
                        <Input
                            size={mobileMode ? "large" : "middle"}
                            placeholder="Location"
                            prefix={<EnvironmentOutlined/>}
                            allowClear={true}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={11} md={11} lg={11}  className="booking-map-filter-form-full-width">
                    <Form.Item name="date-range-picker">
                        <RangePicker
                            {...timePickerProps}
                            placeholder={['Start Time', 'End Time']}
                            onChange={onDateFilterChange}
                            className="booking-map-filter-form-full-width"
                            size={mobileMode ? "large" : "middle"}
                            disabledDate={today => today.isBefore(dayjs().tz().subtract(1,'day'))}
                            allowClear={true}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={3} md={3} lg={3} className="booking-map-filter-form-btn-container">
                    <Form.Item>
                        <Button type="primary" htmlType="submit" className="booking-map-filter-form-btn">Search</Button>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    )
}

export default BookingMapFilterForm;
